/**
 * Connect panel — IoT coordinator overview (admin preview).
 *
 * Slice 1 of folding the standalone coordinator app into OMON (replaces
 * the Flutter `app.stc_coordinator` and the Vite `app.coordinator` v2;
 * see BOFF-029). Renders the caller's in-scope clients as tiles in
 * `#panel-connect`. The per-client sensor swimlane is a later slice —
 * each tile carries a placeholder for it.
 *
 * Data source: the scope-aware `loadPatients()` (the SAME loader the SOMA
 * Plans list uses), narrowed by `filterToIotEocs` to ONLY the EoCs that
 * resolve to a populated `observation.device[]` — i.e. real IoT clients
 * (OMON-209 Phase A). Admins (the only role that can reach this panel today)
 * see all IoT clients; when a scoped coordinator role lands, the loader
 * narrows to careTeam membership automatically, with no change needed here.
 *
 * The filtered list is computed in `switchToTab` and passed in; we cache it
 * at module scope so the timeline's back button can re-render the overview
 * without re-running the (read-heavy) IoT sweep.
 *
 * Gated to admins via `canSeeConnect` (see admin/shell.ts + navigation.ts).
 * That gate is a UX lock, not a security boundary, while prod runs the
 * permissive Firestore rules (OMON-206).
 */
import { escapeHtml } from "../utils/html";
import { go } from "../router";
import type { PatientInfo } from "../types";

/** Last-computed IoT client list — reused on back-nav from the timeline. */
let connectPatients: PatientInfo[] = [];

/** Placeholder shown while `filterToIotEocs` resolves (it reads per EoC). */
export function renderConnectLoading(): void {
  const panel = document.getElementById("panel-connect");
  panel?.classList.remove("connect-wide"); // OMON-290: wide mode is the timeline's
  if (!panel) return;
  panel.innerHTML = `
    <div class="connect-overview">
      <header class="connect-header">
        <h2>Connect — IoT coordinator</h2>
        <p class="connect-sub">Resolving IoT clients…</p>
      </header>
    </div>`;
}

export function renderConnectOverview(patients?: PatientInfo[]): void {
  const panel = document.getElementById("panel-connect");
  panel?.classList.remove("connect-wide"); // OMON-290: wide mode is the timeline's
  if (!panel) return;
  if (patients) connectPatients = patients;

  const tiles = connectPatients
    .map(
      (p) => `
        <div class="card connect-tile" role="button" tabindex="0"
             data-eoc-id="${escapeHtml(p.docId)}"
             data-client-name="${escapeHtml(p.name)}">
          <div class="connect-tile-name">${escapeHtml(p.name)}</div>
          <div class="connect-tile-sub">View sensor timeline →</div>
        </div>`,
    )
    .join("");

  const count = connectPatients.length;
  panel.innerHTML = `
    <div class="connect-overview">
      <header class="connect-header">
        <h2>Connect — IoT coordinator</h2>
        <p class="connect-sub">Admin preview · ${count} IoT client${count === 1 ? "" : "s"} (with registered devices) in scope.</p>
      </header>
      ${
        count
          ? `<div class="connect-grid">${tiles}</div>`
          : `<div class="empty-state"><h3>No IoT clients in scope</h3><p>No clients in scope have registered devices yet.</p></div>`
      }
    </div>`;

  // Tile → per-client sensor timeline. OMON-253: navigate via the hash router
  // (go) so the client view is a real, refresh-safe route (#/connect/<eocId>);
  // applyRoute resolves the name + renders the timeline.
  const open = (el: HTMLElement) => {
    const eocId = el.dataset.eocId;
    if (eocId) go({ kind: "connect-client", eocId });
  };
  panel.querySelectorAll<HTMLElement>(".connect-tile").forEach((el) => {
    el.addEventListener("click", () => open(el));
    el.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        open(el);
      }
    });
  });
}
